import { z } from 'zod';

// Line Item Schema
const lineItemSchema = z.object({
  itemType: z.string().min(1, 'Item type is required'),
  name: z.string().min(1, 'Item name is required'),
  description: z.string().optional(),
  quantity: z.number(),
  unit: z.enum(['period', 'hour', 'day', 'week', 'month', 'year']).default('period'),
  rate: z.number(),
  amount: z.number(),
  sourceReference: z.object({
    agentId: z.string().optional(),
    brandId: z.string().optional(),
    workingDays: z.number().optional(),
    totalHours: z.number().optional(),
    totalMinutes: z.number().optional(),
    totalWorkLogs: z.number().optional(),
    calculatedMethod: z.enum(['hourly', 'daily', 'weekly', 'monthly', 'yearly']).optional(),
  }).optional(),
});

// Trial Adjustment Schema
const trialAdjustmentSchema = z.object({
  trialDays: z.number().min(0).optional(),
  adjustmentAmount: z.number().min(0).optional(),
  note: z.string().optional(),
  applied: z.boolean().default(false),
});

// Bill To Schema
const billToSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Valid email is required'),
  address: z.string().optional(),
  businessName: z.string().optional(),
});

// Service Period Schema
const servicePeriodSchema = z.object({
  startDate: z.string().min(1, 'Start date is required'),
  endDate: z.string().min(1, 'End date is required'),
  display: z.string().optional(),
});

// Main Invoice Schema
export const invoiceFormSchema = z.object({
  // Core fields
  invoiceFor: z.enum(['client', 'agent']).default('client'),
  agentId: z.string().optional(),
  clientId: z.string().optional(),
  currency: z.string().min(1, 'Currency is required').default('USD'),
  
  // Dates
  billDate: z.string().min(1, 'Bill date is required'),
  dueDate: z.string().min(1, 'Due date is required'),
  servicePeriod: servicePeriodSchema,
  
  // Financial fields
  subtotal: z.number().min(0).default(0),
  totalAmount: z.number().min(0).default(0),
  paidAmount: z.number().min(0).default(0),
  
  // Status
  status: z.string().default('pending'),
  
  // Line items and adjustments
  lineItems: z.array(lineItemSchema).default([]),
  trialAdjustment: trialAdjustmentSchema.optional(),
  
  // Bill to information (for clients)
  billTo: billToSchema.optional(),
})
.refine((data) => {
  // Either agentId or clientId must be provided based on invoiceFor
  if (data.invoiceFor === 'agent' && !data.agentId) {
    return false;
  }
  if (data.invoiceFor === 'client' && !data.clientId) {
    return false;
  }
  return true;
}, {
  message: "Agent ID is required for agent invoices, Client ID is required for client invoices",
  path: ['agentId', 'clientId'],
});

// Legacy schema for generate invoice (keeping for backward compatibility)
export const invoiceSchema = z.object({
  bonuses: z.array(z.object({
    name: z.string().optional(),
    amount: z.number().optional(),
    frequency: z.enum(['monthly', 'weekly', 'daily', 'fortnightly', 'one-time', 'specific_day_of_week', 'specific_day_of_month']).optional(),
    dayOfWeek: z.number().optional(),
    dayOfMonth: z.number().optional(),
  })),
  arrears: z.number().min(0, 'Arrears must be greater than or equal to 0'),
});

// Type exports
export type InvoiceFormInputs = z.infer<typeof invoiceFormSchema>;
export type LineItemInputs = z.infer<typeof lineItemSchema>;
export type FormInputs = z.infer<typeof invoiceSchema>; // Legacy type

// Payload types for API
export type CreateInvoicePayload = Omit<InvoiceFormInputs, 'balanceAmount' | 'displayAmount' | 'isOverdue' | 
  'createdAt' | 'updatedAt' | 'workReference' | 'statusBadge' | 'paymentStatusBadge' | 'metadata'
>;

export type UpdateInvoicePayload = Partial<CreateInvoicePayload> & {
  id: string;
};
